<script setup lang="ts">
/**
 * Диалог заведения фичи и диалог её свойств — одна форма в двух ролях.
 *
 * Норма: spec/features/business-model/feature-lifecycle.md.
 *
 * - Заведение (R01, R02): название (обязательно) и описание; статус не
 *   запрашивается — заведённая фича получает `draft`. Отправка допускается
 *   только при непустом названии без учёта окружающих пробелов, и название
 *   уходит обрезанным (naming-R01, R02).
 * - Свойства (R04, R07, R09): диалог открывается **с текущими значениями**;
 *   меняются название, описание и статус — смена статуса часть того же
 *   изменения. Описание, стёртое пользователем, отправляется пустым: это
 *   явная очистка, а не «не передано».
 * - Возврат из архива (R28, features-R14): выбирается статус — любой, кроме
 *   `archived`; название **доступно для правки** — это единственный случай,
 *   когда название архивной фичи меняется, и он избавляет пользователя от
 *   переименования действующей фичи ради освобождения названия. Описание при
 *   возврате не правится.
 * - Успех (R03, R08): диалог закрывается, показывается сообщение. Отказ:
 *   диалог **остаётся открытым**, введённые данные не теряются, пользователю
 *   показывается причина (api-errors-R08).
 * - Отмена (R04): не выполняет ничего и ничего не сообщает.
 */
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  ElButton,
  ElDialog,
  ElForm,
  ElFormItem,
  ElInput,
  ElMessage,
  ElOption,
  ElSelect,
} from "element-plus";

import { useFeaturesStore } from "@/entities/features";
import { useApiErrorMessage } from "@/shared/lib";
import type { FeatureStatus, IFeature } from "@/shared/types";
import { FEATURE_STATUSES } from "@/shared/types";

defineOptions({ name: "MbFeatureFormDialog" });

const props = defineProps<{
  modelValue: boolean;
  /** Проект фичи: операции над фичей ограничены проектом обращения (rbac-R25). */
  projectId: string;
  /** Изменяемая фича; её отсутствие означает заведение новой (R01, R04). */
  feature?: IFeature | null;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  saved: [];
}>();

const { t } = useI18n();
const { showApiError } = useApiErrorMessage();
const featuresStore = useFeaturesStore();

const isEdit = computed<boolean>(
  () => props.feature !== undefined && props.feature !== null,
);

/**
 * Возврат из архива (features-R14, feature-lifecycle-R28): над архивной фичей
 * доступен только он. Название здесь правится — единственный случай, когда
 * название архивной фичи меняется (вердикт 276): занятое прежнее пользователь
 * заменяет прямо тут и возвращает фичу одним действием. Описание не правится,
 * `archived` из выбора статуса убран. Уникальность названия продукт проверяет
 * заново, и занятое даёт конфликт.
 */
const isRestore = computed<boolean>(
  () => props.feature?.status === "archived",
);

/** Статусы, предлагаемые в этом диалоге (features-R05: переходы свободны). */
const statusOptions = computed<readonly FeatureStatus[]>(() =>
  isRestore.value
    ? FEATURE_STATUSES.filter((value) => value !== "archived")
    : FEATURE_STATUSES,
);

const title = computed<string>(() => {
  if (isRestore.value) return t("feature.restoreTitle");
  return isEdit.value ? t("feature.propertiesTitle") : t("feature.createTitle");
});

const name = ref("");
const description = ref("");
const status = ref<FeatureStatus>("draft");

/** Диалог свойств открывается с текущими значениями фичи (R04). */
watch(
  () => [props.modelValue, props.feature] as const,
  ([opened, feature]) => {
    if (!opened) return;
    name.value = feature?.name ?? "";
    description.value = feature?.description ?? "";
    // Возврат из архива требует выбрать, куда возвращать: `archived` в
    // предлагаемых статусах нет (R28), поэтому подставляется черновик.
    status.value =
      feature?.status === "archived" ? "draft" : (feature?.status ?? "draft");
  },
  { immediate: true },
);

/** Отправка допускается только при непустом названии (R02, naming-R01). */
const canSubmit = computed<boolean>(() => name.value.trim().length > 0);

const isBusy = computed<boolean>(() =>
  isEdit.value ? featuresStore.isSaving : featuresStore.isCreating,
);

function close(): void {
  emit("update:modelValue", false);
}

async function submit(): Promise<void> {
  if (!canSubmit.value) return;

  // Нормализация в диалоге не заменяет нормализацию при сохранении, но имя
  // уходит уже без краёв (R02, R23; naming-R01).
  const trimmedName = name.value.trim();
  const feature = props.feature;

  if (feature !== undefined && feature !== null) {
    // Возврат из архива несёт статус и название: описание архивной фичи не
    // правится (R28). Название уходит и когда пользователь его не менял —
    // уникальность продукт проверяет заново по отправленному (features-R14).
    await featuresStore.updateFeature(
      props.projectId,
      feature.id,
      isRestore.value
        ? { name: trimmedName, status: status.value }
        : {
            name: trimmedName,
            // Стёртое описание — явная очистка (R07).
            description: description.value,
            status: status.value,
          },
    );
  } else {
    await featuresStore.createFeature(props.projectId, {
      name: trimmedName,
      // Описание при заведении необязательно (R01): пустое поле — «не задано».
      description:
        description.value.trim().length > 0 ? description.value : undefined,
    });
  }

  // Отказ оставляет диалог открытым и введённые данные на месте (R03, R08).
  if (featuresStore.error !== null) {
    showApiError(featuresStore.error);
    return;
  }

  if (isRestore.value) {
    ElMessage.success(t("feature.restored", { name: trimmedName }));
  } else if (isEdit.value) {
    ElMessage.success(t("feature.saved"));
  } else {
    ElMessage.success(t("feature.created", { name: trimmedName }));
  }
  emit("saved");
  close();
}
</script>

<template>
  <ElDialog
    :model-value="modelValue"
    :title="title"
    width="500px"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <ElForm :label-width="140" @submit.prevent="submit">
      <!--
        Название правится и при возврате из архива: занятое прежнее меняется
        здесь же, одним действием с возвратом (R28, вердикт 276).
      -->
      <ElFormItem :label="t('feature.name')" required>
        <ElInput v-model="name" :placeholder="t('feature.namePlaceholder')" />
      </ElFormItem>
      <!-- Описание при возврате из архива не правится (R28) -->
      <ElFormItem :label="t('feature.description')">
        <ElInput
          v-model="description"
          type="textarea"
          :rows="3"
          :disabled="isRestore"
          :placeholder="t('feature.descriptionPlaceholder')"
        />
      </ElFormItem>
      <!--
        Статус запрашивается только при изменении: заведённая фича получает
        `draft` (R01). Переходы между статусами свободны (features-R05); при
        возврате из архива сам `archived` не предлагается (R28).
      -->
      <ElFormItem v-if="isEdit" :label="t('feature.status')">
        <ElSelect v-model="status" class="feature-form__status">
          <ElOption
            v-for="value in statusOptions"
            :key="value"
            :label="t(`feature.statuses.${value}`)"
            :value="value"
          />
        </ElSelect>
      </ElFormItem>
    </ElForm>
    <template #footer>
      <ElButton :disabled="isBusy" @click="close">
        {{ t("common.cancel") }}
      </ElButton>
      <ElButton
        type="primary"
        :loading="isBusy"
        :disabled="!canSubmit"
        @click="submit"
      >
        {{ isRestore ? t("feature.restore") : isEdit ? t("common.save") : t("common.create") }}
      </ElButton>
    </template>
  </ElDialog>
</template>

<style scoped>
.feature-form__status {
  width: 100%;
}
</style>
